import { formatDurationParts, serverDateTimeToEpoch } from "@/utils/serviceProviderDisplay";

/**
 * The doctor's consultation clock on a visit: Start → (Pause ⇄ Resume) → Finish.
 *
 * ⚠️ THE SERVER OWNS EVERY TIMESTAMP. The client only asks for a transition; the backend stamps
 * `started_at` / `paused_at` / `ended_at` in its own clock and keeps the paused total. The live
 * counter is derived from those stamps, so a reload, a second device or a tab left open overnight
 * all read the same number. See docs/README.visit-consultation-timer-backend-prompt.md.
 *
 * Pure — no Vue, no i18n — so the page, the list and the test script agree.
 */

export type ConsultationTimerState = "idle" | "running" | "paused" | "finished";
export type ConsultationTimerAction = "start" | "pause" | "resume" | "finish";

export interface VisitConsultationTimer {
  state: ConsultationTimerState;
  /** Naive server-zone datetimes (`2026-10-30 10:42:07`), as everywhere else in the backend. */
  startedAt: string | null;
  pausedAt: string | null;
  endedAt: string | null;
  /** Total of the CLOSED pauses. A pause still open is measured from `pausedAt`. */
  pausedSeconds: number;
  /** Server's own figure for a finished timer; preferred over our arithmetic when present. */
  activeSeconds: number | null;
  startedBy: string | null;
  canStart: boolean;
  canPause: boolean;
  canResume: boolean;
  canFinish: boolean;
}

const STATES = new Set<ConsultationTimerState>(["idle", "running", "paused", "finished"]);

function text(value: unknown): string | null {
  const trimmed = String(value ?? "").trim();

  return trimmed || null;
}

function seconds(value: unknown): number | null {
  if (value === null || value === undefined || value === "")
    return null;

  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
}

/** When the backend omits `state`, read it off the stamps — ended beats paused beats started. */
function inferState(startedAt: string | null, pausedAt: string | null, endedAt: string | null): ConsultationTimerState {
  if (endedAt)
    return "finished";
  if (pausedAt)
    return "paused";

  return startedAt ? "running" : "idle";
}

/**
 * The workbench's `consultation_timer` block → typed state.
 *
 * `null` when the block is ABSENT: that is the capability gate. A backend without the feature
 * never sends it, and the page then shows no timer at all rather than buttons that would bounce.
 * The `can_*` flags are read as `=== true` like every other permission on the visit page.
 */
export function normalizeConsultationTimer(raw: unknown): VisitConsultationTimer | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw))
    return null;

  const block = raw as Record<string, unknown>;
  const startedAt = text(block.started_at ?? block.consultation_started_at);
  const pausedAt = text(block.paused_at ?? block.consultation_paused_at);
  const endedAt = text(block.ended_at ?? block.consultation_ended_at);
  const rawState = String(block.state ?? "").trim().toLowerCase() as ConsultationTimerState;

  return {
    state: STATES.has(rawState) ? rawState : inferState(startedAt, pausedAt, endedAt),
    startedAt,
    pausedAt,
    endedAt,
    pausedSeconds: seconds(block.paused_seconds ?? block.consultation_paused_seconds) ?? 0,
    activeSeconds: seconds(block.active_seconds ?? block.consultation_seconds),
    startedBy: text(block.started_by ?? block.consultation_started_by),
    canStart: block.can_start === true,
    canPause: block.can_pause === true,
    canResume: block.can_resume === true,
    canFinish: block.can_finish === true,
  };
}

/**
 * Active consultation time in ms: (end ?? open pause ?? now) − start − closed pauses.
 *
 * `null` before Start. Never negative — a device clock a little behind the server would
 * otherwise count down for the first seconds. A finished timer trusts the server's
 * `activeSeconds` when it sent one, so the chip and the reports can never disagree.
 */
export function consultationActiveMs(
  timer: VisitConsultationTimer | null | undefined,
  nowMs: number,
  zone: string | null,
): number | null {
  if (!timer || timer.state === "idle")
    return null;

  if (timer.state === "finished" && timer.activeSeconds !== null)
    return timer.activeSeconds * 1000;

  const start = serverDateTimeToEpoch(timer.startedAt, zone);
  if (start === null)
    return null;

  const until = timer.state === "finished"
    ? serverDateTimeToEpoch(timer.endedAt, zone)
    : timer.state === "paused"
      ? serverDateTimeToEpoch(timer.pausedAt, zone)
      : nowMs;
  if (until === null)
    return null;

  return Math.max(0, until - start - timer.pausedSeconds * 1000);
}

/** Paused time in ms, the open pause included — for the "paused 8 min" hint. */
export function consultationPausedMs(
  timer: VisitConsultationTimer | null | undefined,
  nowMs: number,
  zone: string | null,
): number {
  if (!timer)
    return 0;

  const closed = timer.pausedSeconds * 1000;
  if (timer.state !== "paused")
    return closed;

  const pausedAt = serverDateTimeToEpoch(timer.pausedAt, zone);

  return closed + (pausedAt === null ? 0 : Math.max(0, nowMs - pausedAt));
}

/**
 * "23 min" / "1h 05m" as an i18n key + params under `alkokh.visitV11.timer.duration`, so the page
 * chip, the list column and any report word a duration the same way.
 */
export function consultationDurationKey(ms: number): { key: string; params: Record<string, number> } {
  if (ms < 60_000)
    return { key: "lessThanMinute", params: {} };

  const { h, m } = formatDurationParts(ms);
  if (h && m)
    return { key: "hm", params: { h, m } };
  if (h)
    return { key: "h", params: { h } };

  return { key: "m", params: { m } };
}

/** `2026-10-30 10:42:07` in the browser's clock — the mock's stand-in for a server stamp. */
export function naiveNow(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** The raw block a backend in `state` would send, flags included. Used by both mocks. */
export function consultationTimerBlock(fields: {
  started_at?: string | null;
  paused_at?: string | null;
  ended_at?: string | null;
  paused_seconds?: number;
  active_seconds?: number | null;
  started_by?: string | null;
} = {}, open = true): Record<string, unknown> {
  const state = inferState(text(fields.started_at), text(fields.paused_at), text(fields.ended_at));

  return {
    state,
    started_at: fields.started_at ?? null,
    paused_at: fields.paused_at ?? null,
    ended_at: fields.ended_at ?? null,
    paused_seconds: fields.paused_seconds ?? 0,
    active_seconds: fields.active_seconds ?? null,
    started_by: fields.started_by ?? null,
    can_start: open && state === "idle",
    can_pause: open && state === "running",
    can_resume: open && state === "paused",
    can_finish: open && (state === "running" || state === "paused"),
  };
}

/**
 * What the backend does for one transition, done in the browser — for the mock page only.
 * Idempotent like the real actions: an action that does not apply returns the block unchanged.
 */
export function applyConsultationTimerAction(
  raw: unknown,
  action: ConsultationTimerAction,
  now: string = naiveNow(),
  user: string | null = null,
): Record<string, unknown> {
  const timer = normalizeConsultationTimer(raw) ?? normalizeConsultationTimer(consultationTimerBlock())!;
  const nowEpoch = serverDateTimeToEpoch(now, null) ?? Date.now();
  const fields = {
    started_at: timer.startedAt,
    paused_at: timer.pausedAt,
    ended_at: timer.endedAt,
    paused_seconds: timer.pausedSeconds,
    active_seconds: timer.activeSeconds,
    started_by: timer.startedBy,
  };
  const closeOpenPause = () => {
    const pausedAt = serverDateTimeToEpoch(fields.paused_at, null);
    if (pausedAt !== null)
      fields.paused_seconds += Math.max(0, Math.round((nowEpoch - pausedAt) / 1000));
    fields.paused_at = null;
  };

  if (action === "start" && timer.state === "idle") {
    fields.started_at = now;
    fields.started_by = user;
  }
  else if (action === "pause" && timer.state === "running") {
    fields.paused_at = now;
  }
  else if (action === "resume" && timer.state === "paused") {
    closeOpenPause();
  }
  else if (action === "finish" && (timer.state === "running" || timer.state === "paused")) {
    closeOpenPause();
    fields.ended_at = now;
    const start = serverDateTimeToEpoch(fields.started_at, null) ?? nowEpoch;
    fields.active_seconds = Math.max(0, Math.round((nowEpoch - start) / 1000) - fields.paused_seconds);
  }

  return consultationTimerBlock(fields);
}
